import { useState, FunctionComponent } from 'react';

import { Typography, Menu, MenuItem } from '@mui/material';
import { TextField, Chip, Box, Button, Checkbox } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { validateV2 } from "model/collection/CollectionValidation";
import { Label } from 'components/common/Label';
import TopAlert from 'components/common/TopAlert';


import { PillContent } from 'components/common/RichTextEditor';
import { TextBox } from 'components/common/TextBox';


import { KeyboardArrowLeft } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import useCollections, { CollectionState } from 'model/collection/CollectionStore';
import { CollectionModel } from 'model/collection/CollectionModels';
import { EXCLAMATION, SMALL_LOADING } from 'assets/Icons';
import { useApi } from 'hooks/useApi';
import { BACKEND_ENDPOINT } from 'utils/Common';

import { getFingerprint } from '@thumbmarkjs/thumbmarkjs'
import dayjs from 'dayjs';
import CryptoJS from 'crypto-js';
import { useAuth0 } from "@auth0/auth0-react";

const emailTemplates = [
  {
    "title": "Friendly Reminder: Upcoming Payment Due",
    "body": "Dear,\n\nWe hope you're doing well! Just a friendly reminder that your payment of $#payment_amount is due.\n\nPlease ensure your payment is made on time. You can make a payment conveniently through any of the payment options provided\n\nIf you've already made your payment, please disregard this message. \n\nThank you!"
  },
  {
    "title": "Payment Reminder: Due Date Approaching",
    "body": "Dear,\n\nWe wanted to remind you that your payment of $#payment_amount is due.\n\nIf you've already made the payment, please ignore this reminder. \n\nBest regards!"
  },
  {
    "title": "Friendly Reminder: Due Date Approaching",
    "body": "Dear,\n\nJust a friendly reminder that you have a payment of $#payment_amount coming due.\n\nIf you've already taken care of this, please disregard this message.\n\nThanks for your attention to this!"
  },
  {
    "title": "Your Favorite Tradition",
    "body": "Hey,\n\nIt's that special time again — no, not a full moon or Mercury in retrograde — I mean the sacred ritual of Paying for your Favorite Service.\n\nJust a friendly nudge from your best friend. Your share is just $#payment_amount\n\nCheers!"
  }
]

// pills are built inside the component so they can reflect the selected schedule

export interface CardProps {
  back: () => void;
  backText: string;
  next: () => void;
  nextText: string;
  isNew: boolean;
  saveLocally: boolean;
  accessToken: string;
  setSaving: (saving: boolean) => void;
}

const FIELDS = new Set(["/title", "/text"])

export const MessageCard: FunctionComponent<CardProps> = ({ back, backText, next, nextText, isNew, saveLocally, accessToken, setSaving }) => {
  const update = useApi({ initialLoading: false });
  const create = useApi({ initialLoading: false });
  const sendTest = useApi({ initialLoading: false });
  const { loginWithRedirect } = useAuth0();

  // Store References
  const formData = useCollections((state: CollectionState) => state.collection);
  const updateCollection = useCollections((state: CollectionState) => state.updateCollection);

  const [violations, setViolations] = useState(new Map());
  const [visited] = useState(isNew ? new Set<string>() : FIELDS);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [templateIndex, setTemplateIndex] = useState(0);

  // anchors for the two Insert dropdowns
  const [anchorTitleMenu, setAnchorTitleMenu] = useState<null | HTMLElement>(null);
  const [anchorMessageMenu, setAnchorMessageMenu] = useState<null | HTMLElement>(null);

  const openTitleMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorTitleMenu(e.currentTarget);
  const closeTitleMenu = () => setAnchorTitleMenu(null);
  const openMessageMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorMessageMenu(e.currentTarget);
  const closeMessageMenu = () => setAnchorMessageMenu(null);

  // Consolidated insert handler for both title and message inputs.
  const handleInsert = (placeHolder: string, field: 'title' | 'text', close: () => void) => {
    const input = document.getElementsByName(field)[0] as (HTMLInputElement | HTMLTextAreaElement | undefined);
    const currentVal = field === 'title' ? (formData.title ?? "") : (formData.text ?? "");
    if (input && typeof input.selectionStart === 'number') {
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? start;
      const newVal = currentVal.slice(0, start) + placeHolder + currentVal.slice(end);
      updateCollection(field, newVal);
      const caretPos = start + placeHolder.length;
      input.focus();
      setTimeout(() => {
        try { input.setSelectionRange(caretPos, caretPos); } catch (e) { /* noop */ }
      }, 0);
    } else {
      const newVal = `${currentVal} ${placeHolder}`.trim();
      updateCollection(field, newVal);
    }
    close();
  };

  // Build pills dynamically so the Month/Day pill reflects the selected schedule
  const pills: PillContent[] = [
    { label: "Payment Amount", placeHolder: "#payment_amount", property: "amount" },
    { label: "Schedule", placeHolder: "#schedule", property: "schedule" },
    { label: "Recipient", placeHolder: "#recipient", property: "recipient" }
  ];

  if (formData.schedule === "monthly") {
    pills.push({ label: "Month", placeHolder: "#month", property: "dayOfTheMonth" });
  } else if (formData.schedule === "weekly") {
    pills.push({ label: "Day of the Week", placeHolder: "#day_of_the_week", property: "dayOfTheWeek" });
  }

  const messageReplacements = new Map(pills.map((pill) => {
    const prop = pill.property as keyof CollectionModel;
    return [pill.placeHolder, formData[prop]]
  }))

  const handleChange = (event: any) => {
    if (!event.target)
      return;

    const name = event.target.name;
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;

    updateCollection(name, value);
  };

  const handleTextGeneration = () => {
    const n = (templateIndex + 1) % emailTemplates.length;
    const template = emailTemplates[n];

    Object.assign(formData, { text: template.body.replaceAll("#payment_amount", formData.amount.toString()), title: template.title });

    ["/title", "/text"].forEach(path => visited.add(path));
    setViolations(validateV2(formData, visited));

    setTemplateIndex(n);
  };

  const handleBlur = (el: any) => {
    visited.add(`/${el.target.name}`);
    let errors = validateV2(formData, visited);

    setViolations(errors);
  }

  const handleSave = async () => {
    setSaving(true);
    FIELDS.forEach(s => visited.add(s))

    let errors = validateV2(formData, visited);
    setViolations(errors);
    console.log("Violations: ", errors);
    if (errors.size > 0) {
      setSaving(false);
      return;
    }

    if (saveLocally) {
      formData.clientDateTime = dayjs().toISOString();
      // Save the collection temporarily
      getFingerprint().then((key) => {
        const ciphertext = CryptoJS.AES.encrypt(JSON.stringify(formData), key).toString();
        localStorage.setItem("tempCollection", ciphertext);
        // TODO: The idea here is that we only save locally
        // as a cache for the user to signup. At some point
        // we might want to have a full local mode
        loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } }).then(() => {
          // This is a bit of a hack, for the getting started flow
          // The problem is that the signup button gets enabled before
          // the redirect. Well, we dont need enable the signup button
          // if we are gonna redirect anyways
          //setLoadingStatus(false);
        });
      })
      return;
    }

    try {
      if (isNew) {
        await create.request(() =>
          fetch(BACKEND_ENDPOINT + "collecting", {
            method: 'POST',
            body: JSON.stringify(formData),
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            }
          })
        );
      } else {
        await update.request(() =>
          fetch(BACKEND_ENDPOINT + "collecting", {
            method: 'PUT',
            body: JSON.stringify(formData),
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            }
          })
        );
      }
    } catch (err) {
      console.error("Failed to save collection", err);
      const apiError = isNew ? create.error : update.error;
      setErrorMessage(apiError || "Failed to save collection");
      setSaving(false);
      return;
    }
    setSaving(false);
    next();
  }

  const apiError = isNew ? create.error : update.error;

  return (<>
    <TopAlert message={errorMessage} severity="error" setMessage={setErrorMessage} />
    <Chip disabled={update.loading || create.loading}
      style={{ float: "right" }}
      label={<>Suggestions  <AutorenewIcon style={{ height: "18px", width: "18px", marginBottom: "-4px" }} /></>}
      variant="outlined" className='chip'
      onClick={handleTextGeneration} />
    <Typography variant='h5'>Message</Typography>
    <div style={{ marginBottom: "20px" }}>
      <Typography variant='body1' color="secondary" component="span">
        A friendly title and message can go a long ways. &nbsp;
      </Typography>
    </div>

    <Chip disabled={update.loading || create.loading}
      style={{ float: "right" }}
      label={<>Insert  <ExpandMoreIcon style={{ height: "18px", width: "18px", marginBottom: "-4px" }} /></>}
      variant="outlined" className='chip'
      onClick={openTitleMenu} />
    <Menu
      anchorEl={anchorTitleMenu}
      open={Boolean(anchorTitleMenu)}
      onClose={closeTitleMenu}
      MenuListProps={{ dense: true }}>
      {pills.map(p => (
        <MenuItem key={p.placeHolder} onClick={() => handleInsert(p.placeHolder, 'title', closeTitleMenu)}>
          {p.label}
        </MenuItem>
      ))}
    </Menu>

    <TextBox
      focused={true}
      name="title"
      label="Email Title"
      value={formData.title}
      onBlur={handleBlur}
      onChange={handleChange}
      error={violations?.get("/title")}
      helpText="The email title"
      disabled={update.loading || create.loading}
      maxLength={250} />

    <Chip disabled={update.loading || create.loading}
      style={{ float: "right" }}
      label={<>Insert  <ExpandMoreIcon style={{ height: "18px", width: "18px", marginBottom: "-4px" }} /></>}
      variant="outlined" className='chip'
      onClick={openMessageMenu} />
    <Menu
      anchorEl={anchorMessageMenu}
      open={Boolean(anchorMessageMenu)}
      onClose={closeMessageMenu}
      MenuListProps={{ dense: true }}>
      {pills.map(p => (
        <MenuItem key={p.placeHolder} onClick={() => handleInsert(p.placeHolder, 'text', closeMessageMenu)}>
          {p.label}
        </MenuItem>
      ))}
    </Menu>

    <Label label="Message"/>
    <TextField required
      sx={{ mx: 0, mt: "2px", mb: violations?.get("/text") ? "7px" : "32px" }}
      fullWidth
      name="text"
      multiline
      value={formData.text}
      error={violations?.get("/text")}
      minRows={10}
      maxRows={10}
      onBlur={handleBlur}
      disabled={update.loading || create.loading}
      helperText={violations?.get("/text")}
      onChange={handleChange}>
    </TextField>

    <TextBox
      name="onBehalfOf"
      label="On Behalf Of (optional)"
      value={formData.onBehalfOf ?? ""}
      onChange={handleChange}
      maxLength={250}
      disabled={update.loading || create.loading}
      helpText="The email will be sent on behalf of"
      infoBox="If provided, the email will be sent on behalf of the specified individual or organization. This can help personalize the communication and provide clarity to the recipient about who is contacting them." />


    {/* <Box>
      <Grid container spacing={1}>
        <Grid size={{ xs: 'grow', md: 5 }}>
          <Typography variant='body1'>Send me a copy of the email every time</Typography>
        </Grid>
        <Grid size={{ xs: 'auto', md: 1 }}>
          <Switch sx={{marginTop: "-8px"}} 
                  onChange={handleChange} 
                  name="sendCopy" 
                  checked={formData.sendCopy}/>
        </Grid>
        <Grid size={{ xs: 0, sm: 5 }}>
        </Grid>
      </Grid>
      <Grid container spacing={1} sx={{marginBottom: "15px"}}>
        <Grid size={{ xs: 'grow', md: 5 }}>
          <Typography color="secondary" variant='body1'>Create an invoice template for this collection</Typography>
        </Grid>
        <Grid size={{ xs: 'auto', md: 1 }}>
          <Switch sx={{marginTop: "-8px"}}></Switch>
        </Grid>
        <Grid size={{ xs: 0, sm: 5 }}>
        </Grid>
      </Grid> 
    </Box>
    
    
    New Collection Flow - existing customer:
      1. The checkbox will be checked by default
      2. If user unchecks it, there will be warning
      on the collection (in the list, and in dashboard)

    New Collection Flow - new customer:
      1. Customer goes to signup
      2. Then on the dashboard we show a warning, when clicked tooltip show error, and say to click edit
W        
    */}
    <Box sx={{mt: -1}}>
      <Grid container spacing={1} alignItems="center">
        <Grid component="label" htmlFor="verify" sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            id="verify"
            onChange={handleChange}
            name="verify"
            checked={formData.verify}
            color="primary"
          />
          <Typography variant='body1'>Send me a copy to verify my changes</Typography>
        </Grid>
      </Grid>
    </Box>

    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
      <Button onClick={back} color="inherit" sx={{ mr: 1 }} disabled={update.loading || create.loading}>
        <KeyboardArrowLeft />{backText}
      </Button>
      <Box sx={{ flex: '1 1 auto' }} />

      <Button onClick={handleSave} color="inherit" disabled={update.loading || create.loading}>
        {update.loading || create.loading ? SMALL_LOADING(20) : nextText}
      </Button>
    </Box>
  </>);
}
export default MessageCard;